// CSV export of the "cost per partner × mode" table — the granular billing
// basis. Downloaded through `downloadCsv` (`lib/export.ts`), so it opens
// natively in Excel with Swiss umlauts intact.
//
// The figures are LLM spend priced from the stored token counts, not an
// invoice: title / recap / search-plan calls create no turn and infrastructure is not
// attributable per partner. The header row says so, and the export carries
// the window and the applied partner filter so a sheet is self-describing.

import { t } from "@/i18n";
import type { TenantCostRow } from "@/lib/costRows";
import { usdPerSession, usdPerTurn } from "@/lib/costRows";
import { csvRow, downloadCsv } from "@/lib/export";
import { modeLabel, tenantLabel } from "@/lib/format";

export interface CostExportMeta {
  windowLabel: string;
  /** Applied partner filter; "" for all traffic. */
  tenantFilter: string;
  /** ISO timestamp the payload was produced at (backend `timestamp`). */
  generatedAt: string;
}

// Money to two decimals with a period — the currency column is machine-read
// by the spreadsheet, so no thousands separator here.
const money = (usd: number): string => usd.toFixed(2);

/** Build the CSV: a short metadata block, a blank line, then one row per
 * partner total and one per partner × mode. Column headers follow the active
 * dashboard language. */
export function buildTenantCostCsv(rows: TenantCostRow[], meta: CostExportMeta): string {
  const metaRows: Array<[string, string]> = [
    [t("cost.export.meta.window"), meta.windowLabel],
    [t("cost.export.meta.filter"), tenantLabel(meta.tenantFilter)],
    [t("cost.export.meta.generated"), meta.generatedAt],
    [t("cost.export.meta.basis"), t("cost.export.meta.basisNote")],
  ];
  const lines = metaRows.map(csvRow);
  lines.push("");

  const headers = [
    t("cost.export.col.partner"),
    t("cost.export.col.mode"),
    t("cost.export.col.model"),
    t("cost.export.col.turns"),
    t("cost.export.col.sessions"),
    t("cost.export.col.inputTokens"),
    t("cost.export.col.outputTokens"),
    t("cost.export.col.cacheRead"),
    t("cost.export.col.cacheCreate"),
    t("cost.export.col.costUsd"),
    t("cost.export.col.perTurn"),
    t("cost.export.col.perSession"),
  ];
  lines.push(csvRow(headers));

  for (const row of rows) {
    const s = row.stats;
    lines.push(
      csvRow([
        tenantLabel(row.tenant),
        row.kind === "tenant" ? t("cost.export.total") : modeLabel(row.mode ?? ""),
        s.model_label || s.model || "",
        s.turns,
        s.sessions,
        s.input_tokens,
        s.output_tokens,
        s.cache_read_tokens,
        s.cache_create_tokens,
        money(s.total_usd),
        money(usdPerTurn(s)),
        money(usdPerSession(s)),
      ]),
    );
  }
  return lines.join("\r\n");
}

/** File name — `kosten-partner-<window>.csv`, e.g. `kosten-partner-30d.csv`.
 * Deliberately NOT localized: a billing artefact is filed and re-found by a
 * stable name, and the same window must produce the same file name whichever
 * language the operator happened to be reading in. */
export function tenantCostCsvFilename(meta: CostExportMeta): string {
  return `kosten-partner-${meta.windowLabel}.csv`;
}

/** Download the table as an Excel-openable CSV. */
export function downloadTenantCostCsv(rows: TenantCostRow[], meta: CostExportMeta): void {
  downloadCsv(tenantCostCsvFilename(meta), buildTenantCostCsv(rows, meta));
}
